import React from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { createSearchParams, useNavigate } from "react-router-dom";

const CurrentInscriptionsContainer = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 40px;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
    animation: pulse 4s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 0.5; }
    50% { transform: scale(1.1); opacity: 0.8; }
  }
`;

const InscriptionTitle = styled.h3`
  color: white;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  position: relative;

  &:before {
    content: "🔔";
    margin-right: 12px;
    font-size: 24px;
    animation: ring 2s ease-in-out infinite;
  }

  @keyframes ring {
    0%, 100% { transform: rotate(0); }
    10%, 30% { transform: rotate(-10deg); }
    20%, 40% { transform: rotate(10deg); }
  }
`;

const NoInscriptionsMessage = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 15px;
  text-align: center;
  padding: 20px 0;
  margin: 0;
`;

const CurrentInscriptionsSection = ({ events }) => {
  const navigate = useNavigate();
  
  // Filter for events currently accepting inscriptions
  const currentInscriptionEvents = events.filter(event => {
    const now = new Date();
    const inscriptionsStartDate = new Date(event.inscriptions_start_date);
    const inscriptionsEndDate = new Date(event.inscriptions_end_date);
    inscriptionsEndDate.setHours(23, 59, 59);
    
    return inscriptionsStartDate <= now && inscriptionsEndDate >= now;
  });
  
  // Sort events by end date (closest end dates first)
  const sortedEvents = [...currentInscriptionEvents].sort((a, b) => {
    return new Date(a.inscriptions_end_date) - new Date(b.inscriptions_end_date);
  });

  function navigateToEventEditor(event) {
    const path = event.type === "fd100" ? "/admin/events/fdeditevent" : "editevent";
    navigate({
      pathname: path,
      search: createSearchParams({id: event._id}).toString()
    });
  }

  function navigateToEventManagement(event) {
    if (event.type === "fd100") {
      navigate({
        pathname: "/admin/events/fdadmin",
        search: createSearchParams({eid: event._id}).toString()
      });
    } else if (event.type === "design") {
      // Check if this is a Milan 2026 design event
      const isMilan2026 = event.title_fr?.toLowerCase().includes('milan 2026') || 
                         event.title_fr?.toLowerCase().includes('milan2026');
      
      if (isMilan2026) {
        navigate({
          pathname: "/admin/events/fd100projects-modern",
          search: createSearchParams({eid: event._id}).toString()
        });
      } else {
        navigate({
          pathname: "shortlists",
          search: createSearchParams({eid: event._id}).toString()
        });
      }
    } else {
      navigate({
        pathname: "shortlists",
        search: createSearchParams({eid: event._id}).toString()
      });
    }
  }
  
  function navigateToCompilationJury(event) {
    navigate({
      pathname: "/admin/events/fdcompilationjury",
      search: createSearchParams({eid: event._id}).toString()
    });
  }
  
  return (
    <CurrentInscriptionsContainer>
      <InscriptionTitle>Événements avec inscriptions en cours</InscriptionTitle>
      
      {sortedEvents.length > 0 ? (
        sortedEvents.map((event, index) => (
          <EventLineContainer key={index}>
            <div>
              <strong>{event.title_fr}</strong>
              <div className='thin'>
                Fin des inscriptions : {dayjs(event.inscriptions_end_date).format('dddd D MMMM YYYY')}
              </div>
              <EditButton onClick={() => navigateToEventEditor(event)}>
                Modifier <span className='crayon'>✎</span>
              </EditButton>
            </div>
            <StatusBadge>Inscriptions en cours</StatusBadge>
            <div className='button-column'>
              <ActionButton primary onClick={() => navigateToEventManagement(event)}>
                {event.type === "fd100" ? "Candidats" : event.type === "design" ? "Candidatures" : "Sociétés"}
              </ActionButton>
              {event.type === "fd100" ? (
                <ActionButton onClick={() => navigateToCompilationJury(event)}>Compilation jury</ActionButton>
              ) : event.type !== "design" && (
                <ActionButton onClick={() => window.open("events/planning?eid="+event._id)}>Matching</ActionButton>
              )}
            </div>
          </EventLineContainer>
        ))
      ) : (
        <NoInscriptionsMessage>Aucun événement n'accepte d'inscriptions en ce moment.</NoInscriptionsMessage>
      )}
    </CurrentInscriptionsContainer>
  );
};

const EventLineContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  margin: 8px 0;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    transform: translateX(4px);
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }

  & > div:first-child {
    flex: 2;
    padding-right: 16px;

    strong {
      color: #2c3e50;
      font-size: 15px;
      font-weight: 600;
      display: block;
      margin-bottom: 4px;
    }

    .thin {
      color: #7f8c8d;
      font-size: 13px;
      font-weight: 400;
    }
  }

  .button-column {
    flex: 1.5;
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #27ae60;
  flex: 1;
  justify-content: center;
`;

const EditButton = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #3498db, #2980b9);
  color: white;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  justify-content: center;
  margin-top: 8px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(52, 152, 219, 0.3);
  }

  .crayon {
    font-size: 16px;
  }
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  background: ${props => props.primary ?
    'linear-gradient(135deg, #9b59b6, #8e44ad)' :
    'linear-gradient(135deg, #ecf0f1, #bdc3c7)'};
  color: ${props => props.primary ? 'white' : '#2c3e50'};
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }
`;

export default CurrentInscriptionsSection;